import { computed } from "vue";
import { useRoute, useRouter } from "vue-router";

import { useReleaseStore } from "@/stores/release-store";
import { useThreadsStore } from "@/stores/threads-store";

/**
 * Shared state + action for the "viewing a thread branch" chrome — the top-bar
 * thread pill and the sidebar worktree card both read from here so the branch
 * resolution and the exit live in one place instead of being duplicated.
 *
 * The active-thread *id* lives on the release store (it drives revision-aware
 * reads), while the thread's title/description live on the threads store. This
 * bridges the two: it prefers the fully-hydrated `activeThread` and falls back
 * to the eagerly-loaded thread list, so the chrome has a title/description even
 * before the drawer's detail view connects and fetches the thread.
 */
export const useThreadBranchView = () => {
    const route = useRoute();
    const router = useRouter();
    const releaseStore = useReleaseStore();
    const threadsStore = useThreadsStore();

    const viewingThreadBranch = computed(
        () => releaseStore.activeThreadId !== null
    );

    const branchThread = computed(() => {
        const id = releaseStore.activeThreadId;
        if (!id) return undefined;
        const active = threadsStore.activeThread;
        if (active?.identity === id) return active;
        return threadsStore.threads.find((t) => t.identity === id);
    });

    // Catch-up conflicts on the branch being viewed. Read off branchThread (not
    // the store's activeThread-only computeds) so the count is correct whichever
    // side resolved the thread. Pending = still needs a pick-a-side (blocks
    // Accept); resolved = decided but not yet accepted (revertible, so still worth
    // a review jump). Empty until the drawer hydrates activeThread with conflicts.
    const conflicts = computed(() => branchThread.value?.conflicts ?? []);
    const pendingConflicts = computed(() =>
        conflicts.value.filter((c) => c.status === "pending")
    );
    const resolvedConflicts = computed(() =>
        conflicts.value.filter((c) => c.status === "resolved")
    );

    const branchTitle = computed(
        () => branchThread.value?.title ?? "Thread branch"
    );

    // Second line under the title. Prefer the author's description; fall back to
    // the auto-generated change summary (only present once resolved), then to a
    // generic staged-changes note. An LLM "current direction" summary can slot in
    // here later without touching the chrome that renders it.
    const branchSubtitle = computed(() => {
        const t = branchThread.value;
        return (
            t?.description ||
            t?.change_summary ||
            "Staged changes — not yet applied"
        );
    });

    // "Back to working copy": drop ?thread= (the ApplicationLayout watcher then
    // clears the open-thread state) and clear it here too so the chrome updates
    // immediately, before the navigation resolves.
    const exitThreadBranchView = () => {
        releaseStore.clearOpenThreadId();
        const { thread: _omit, ...query } = route.query;
        void router.replace({ query });
    };

    // Jump to the full-page conflict resolution ("Reconcile") for this branch —
    // the same target as the drawer's conflict CTAs (thread carried via ?thread=,
    // stek params reused from the current route).
    const goToReconcile = () => {
        const id = releaseStore.activeThreadId;
        if (!id) return;
        void router.push({ name: "reconcile", query: { thread: id } });
    };

    return {
        viewingThreadBranch,
        branchThread,
        branchTitle,
        branchSubtitle,
        conflicts,
        pendingConflicts,
        resolvedConflicts,
        exitThreadBranchView,
        goToReconcile,
    };
};
